import React from 'react';

const AUTH_STORAGE_KEY = 'ev-dashboard-auth';

const AuthContext = React.createContext(null);

function readStoredSession() {
    try {
        const rawSession = localStorage.getItem(AUTH_STORAGE_KEY);

        if (!rawSession) {
            return null;
        }

        const parsedSession = JSON.parse(rawSession);

        if (!parsedSession?.userId) {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            return null;
        }

        return parsedSession;
    } catch (error) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
    }
}

export function AuthProvider({ children }) {
    const [session, setSession] = React.useState(() => readStoredSession());

    const login = React.useCallback((userId) => {
        const nextSession = {
            userId,
            loggedInAt: new Date().toISOString(),
        };

        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
        setSession(nextSession);
    }, []);

    const logout = React.useCallback(() => {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setSession(null);
    }, []);

    const value = React.useMemo(() => ({
        isAuthenticated: Boolean(session?.userId),
        session,
        login,
        logout,
    }), [login, logout, session]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = React.useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}

export { AUTH_STORAGE_KEY };